import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from "@mui/material";

import { useAnalytics } from "@/providers/analytics";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { LayoutRecord } from "@/shared/domain/layouts";
import { useUpdateLayout } from "@/shared/domain/layouts/hooks";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";
import { RobotoDomainException } from "@/shared/http";

interface ShareLayoutConfirmationProps {
  layout: LayoutRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onExited: () => void;
}

export function ShareLayoutConfirmation(props: ShareLayoutConfirmationProps) {
  const { layout, isOpen, onClose, onExited } = props;

  const { trackEvent } = useAnalytics();

  const updateLayout = useUpdateLayout();

  const confirmShareLayout = () => {
    if (layout) {
      updateLayout.mutate(
        {
          layoutId: layout.layout_id,
          updates: {
            accessibility: LayoutAccessibility.Organization,
          },
        },
        {
          onSuccess: onClose,
        },
      );
    }
  };

  return (
    <Dialog
      disableRestoreFocus
      open={isOpen && layout !== null}
      TransitionProps={{ onExited }}
      onClose={() => {
        trackEvent("ShareLayoutConfirmationCanceled", {
          layoutId: layout?.layout_id ?? "",
          layoutName: layout?.name ?? "",
        });
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Share &quot;{layout?.name ?? "Layout"}&quot;</DialogTitle>
      <DialogContent>
        Are you sure you want to share this layout? This action cannot be
        undone.
        {updateLayout.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formatErrorMessage(updateLayout.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <RobotoButton
          eventName="ShareLayoutConfirmationCanceled"
          eventProperties={{
            layoutId: layout?.layout_id ?? "",
            layoutName: layout?.name ?? "",
          }}
          onClick={onClose}
          disabled={updateLayout.isPending}
        >
          Cancel
        </RobotoButton>
        <RobotoButton
          eventName="ShareLayoutConfirmationClicked"
          eventProperties={{
            layoutId: layout?.layout_id ?? "",
            layoutName: layout?.name ?? "",
          }}
          onClick={confirmShareLayout}
          color="primary"
          variant="contained"
          disabled={updateLayout.isPending}
          startIcon={
            updateLayout.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {updateLayout.isError ? "Retry" : "Share"}
        </RobotoButton>
      </DialogActions>
    </Dialog>
  );
}

function formatErrorMessage(error: unknown): string {
  // If it's a client error, we can show the user the message
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return error.message;
  }

  return "Failed to share layout. Try again.";
}
